import { useRef, useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  CheckCircle, RefreshCw, Trash2,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ChevronDown, ChevronRight as ChevronRightIcon,
  List, Layers, Sparkles,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import { formatDuration } from '../../utils/format-duration';
import type { IrregularDeltaItem, HandleType } from '../../types/data-error-types';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// ── Grouping ──────────────────────────────────────────────────────────────────

interface MachineGroup {
  machineId: number;
  machineName: string;
  items: IrregularDeltaItem[];
}

function groupByMachine(items: IrregularDeltaItem[]): MachineGroup[] {
  const map = new Map<number, MachineGroup>();
  for (const item of items) {
    if (!map.has(item.machineId)) {
      map.set(item.machineId, {
        machineId: item.machineId,
        machineName: item.machineName || `Machine #${item.machineId}`,
        items: [],
      });
    }
    map.get(item.machineId)!.items.push(item);
  }
  return Array.from(map.values());
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ item }: { item: IrregularDeltaItem }) {
  const { statusId, handleType } = item;

  let label: string;
  let cls: string;

  if (statusId === 'Pending') {
    label = 'Pending';
    cls = 'bg-yellow-100 text-yellow-700';
  } else if (statusId === 'Approved' && handleType === 'Replacement') {
    label = 'Replacement';
    cls = 'bg-blue-100 text-blue-700';
  } else if (statusId === 'Approved' && handleType === 'ManualCorrection') {
    label = 'Manual Correction';
    cls = 'bg-purple-100 text-purple-700';
  } else if (statusId === 'Approved') {
    label = 'Approved';
    cls = 'bg-green-100 text-green-700';
  } else if (statusId === 'Rejected') {
    label = 'Ignored';
    cls = 'bg-gray-100 text-gray-600';
  } else {
    label = 'Deleted';
    cls = 'bg-red-100 text-red-700';
  }

  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

// ── Action buttons per row ────────────────────────────────────────────────────

interface ActionButtonsProps {
  item: IrregularDeltaItem;
  handlingId: number | null;
  onHandle: (irregularId: number, type: HandleType) => void;
}

function ActionButtons({ item, handlingId, onHandle }: ActionButtonsProps) {
  if (item.handled) return null;

  const isDisabled = handlingId === item.irregularId;

  const buttons: { type: HandleType; icon: React.ReactNode; cls: string; label: string }[] = [
    { type: 'Approve',     icon: <CheckCircle className="h-4 w-4" />, cls: 'text-green-600 hover:text-green-700 hover:bg-green-50', label: 'Approve' },
    { type: 'Replacement', icon: <RefreshCw className="h-4 w-4" />,  cls: 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',   label: 'Mark Replacement' },
    { type: 'Delete',      icon: <Trash2 className="h-4 w-4" />,     cls: 'text-red-500 hover:text-red-700 hover:bg-red-50',      label: 'Delete' },
  ];

  return (
    <div className="flex items-center gap-1">
      {buttons
        .filter(b => item.handleType !== b.type)
        .map(b => (
          <Button
            key={b.type}
            variant="ghost"
            size="sm"
            title={b.label}
            disabled={isDisabled}
            className={cn('h-7 w-7 p-0 rounded', b.cls)}
            onClick={e => { e.stopPropagation(); onHandle(item.irregularId, b.type); }}
          >
            {b.icon}
          </Button>
        ))}
    </div>
  );
}

// ── Group header row ──────────────────────────────────────────────────────────

interface GroupHeaderRowProps {
  group: MachineGroup;
  colSpan: number;
  expanded: boolean;
  onToggle: () => void;
  onAnalyzeAll?: (items: IrregularDeltaItem[]) => void;
}

function GroupHeaderRow({ group, colSpan, expanded, onToggle, onAnalyzeAll }: GroupHeaderRowProps) {
  const pendingCount = group.items.filter(i => !i.handled).length;
  const anomalousCount = group.items.filter(i => i.delta < 0 || Math.abs(i.delta) > 50).length;

  return (
    <tr className="bg-[#EBEBEB] select-none">
      <td colSpan={colSpan} className="px-4 py-2.5 border-t border-b border-t-[#D0D0D0] border-b-[#D0D0D0]">
        <div className="flex items-center gap-2.5">
          <button onClick={onToggle} className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer hover:opacity-70 text-left">
            <span className="text-[#606060] shrink-0">
              {expanded
                ? <ChevronDown className="h-4 w-4" />
                : <ChevronRightIcon className="h-4 w-4" />
              }
            </span>
            <span className="font-semibold text-sm text-[#262626] truncate">{group.machineName}</span>
            <span className="text-xs text-[#909090] font-mono shrink-0">#{group.machineId}</span>
            <span className="ml-1 inline-flex items-center rounded-full bg-[#E8E8E8] px-2 py-0.5 text-xs font-medium text-[#606060] shrink-0">
              {group.items.length} record{group.items.length !== 1 ? 's' : ''}
            </span>
            {pendingCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 shrink-0">
                {pendingCount} pending
              </span>
            )}
            {anomalousCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600 shrink-0">
                {anomalousCount} anomalous
              </span>
            )}
          </button>
          {onAnalyzeAll && pendingCount > 0 && (
            <button
              onClick={e => { e.stopPropagation(); onAnalyzeAll(group.items); }}
              className="ml-auto shrink-0 flex items-center gap-1 text-xs font-medium text-[#606060] border border-[#E8E8E8] rounded px-2 py-1 bg-white hover:border-[#FFCD00] hover:text-[#262626] transition-colors"
            >
              <Sparkles className="h-3 w-3 text-amber-500" />
              Analyze all
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  items: IrregularDeltaItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  selectedItemId: number | null;
  handlingId: number | null;
  checkedIds: Set<number>;
  grouped: boolean;
  onGroupedChange: (grouped: boolean) => void;
  onRowClick: (item: IrregularDeltaItem) => void;
  onHandle: (irregularId: number, type: HandleType) => void;
  onCommentBlur: (irregularId: number, comment: string) => void;
  onToggleChecked: (id: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onAnalyzeAll?: (items: IrregularDeltaItem[]) => void;
}

const columnHelper = createColumnHelper<IrregularDeltaItem>();

export function IrregularDeltaTable({
  items,
  totalCount,
  currentPage,
  pageSize,
  isLoading,
  error,
  selectedItemId,
  handlingId,
  checkedIds,
  grouped,
  onGroupedChange,
  onRowClick,
  onHandle,
  onCommentBlur,
  onToggleChecked,
  onPageChange,
  onPageSizeChange,
  onAnalyzeAll,
}: Props) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  // Group rows by machine — recompute only when items change
  const groups = useMemo(() => groupByMachine(items), [items]);

  // Expanded group state — default all collapsed
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (machineId: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  };

  // Per-row comment refs to avoid controlled-input re-render issues
  const commentRefs = useRef<Record<number, string>>({});

  const columns = [
    columnHelper.display({
      id: 'checkbox',
      header: () => null,
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-[#E8E8E8] accent-[#FFCD00]"
          checked={checkedIds.has(row.original.irregularId)}
          onChange={e => { e.stopPropagation(); onToggleChecked(row.original.irregularId); }}
          onClick={e => e.stopPropagation()}
        />
      ),
      size: 40,
    }),
    columnHelper.accessor('irregularId', {
      header: 'ID',
      cell: info => <span className="font-mono text-sm">{info.getValue()}</span>,
      size: 80,
    }),
    columnHelper.display({
      id: 'station',
      header: 'Charging Station',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-[#262626] text-sm">{row.original.machineName || '—'}</div>
          <div className="text-xs text-[#606060]">#{row.original.machineId}</div>
        </div>
      ),
      size: 160,
    }),
    columnHelper.accessor('connectorId', {
      header: 'Connector',
      cell: info => <span className="text-sm">{info.getValue()}</span>,
      size: 90,
    }),
    columnHelper.accessor('physicalReference', {
      header: 'Physical Ref',
      cell: info => <span className="text-sm text-[#606060]">{info.getValue() ?? '—'}</span>,
      size: 120,
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created',
      cell: info => <span className="text-sm whitespace-nowrap">{format(info.getValue(), 'dd/MM/yyyy HH:mm')}</span>,
      size: 140,
    }),
    columnHelper.accessor('durationSeconds', {
      header: 'Duration',
      cell: info => <span className="text-sm">{formatDuration(info.getValue())}</span>,
      size: 90,
    }),
    columnHelper.accessor('fromEnergy', {
      header: 'Energy Before',
      cell: info => <span className="text-sm font-mono">{info.getValue().toFixed(3)} kWh</span>,
      size: 110,
    }),
    columnHelper.accessor('toEnergy', {
      header: 'Energy After',
      cell: info => <span className="text-sm font-mono">{info.getValue().toFixed(3)} kWh</span>,
      size: 110,
    }),
    columnHelper.accessor('delta', {
      header: 'Delta',
      cell: info => {
        const v = info.getValue();
        const isAnomalous = v < 0 || Math.abs(v) > 50;
        return (
          <span className={cn('text-sm font-mono', isAnomalous && 'text-red-600 font-semibold')}>
            {v.toFixed(3)} kWh
          </span>
        );
      },
      size: 110,
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge item={row.original} />,
      size: 130,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <ActionButtons item={row.original} handlingId={handlingId} onHandle={onHandle} />
      ),
      size: 150,
    }),
    columnHelper.display({
      id: 'notes',
      header: 'Notes',
      cell: ({ row }) => {
        const id = row.original.irregularId;
        if (!(id in commentRefs.current)) {
          commentRefs.current[id] = row.original.comments ?? '';
        }
        return (
          <input
            key={`${id}-${row.original.comments}`}
            className="w-full min-w-[120px] text-sm border border-[#E8E8E8] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#FFCD00]"
            defaultValue={row.original.comments ?? ''}
            onClick={e => e.stopPropagation()}
            onChange={e => { commentRefs.current[id] = e.target.value; }}
            onBlur={e => { e.stopPropagation(); onCommentBlur(id, e.target.value); }}
          />
        );
      },
      size: 200,
    }),
  ];

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // Build a fast lookup: irregularId → TanStack row (for cell rendering)
  const rowById = useMemo(
    () => new Map(table.getRowModel().rows.map(r => [r.original.irregularId, r])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const colCount = table.getAllLeafColumns().length;

  // ── States ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-col h-full border-x border-t bg-white rounded-b-none">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={cn('flex items-center gap-4 px-4 py-3 border-b border-[#E8E8E8]', i === 0 && 'bg-[#F1F1F1]')}>
            {[40, 80, 160, 90, 120, 140, 90, 110].map((w, j) => (
              <div key={j} className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: w }} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-[#E8E8E8] rounded bg-white">
        <p className="text-sm text-red-600 mb-3">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-[#E8E8E8] rounded bg-white">
        <p className="text-[#606060] font-medium">No irregular delta records found</p>
        <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  // ── Pagination helpers ──────────────────────────────────────────────────────

  const pageNumbers: number[] = [];
  const start = Math.max(0, currentPage - 1);
  const end = Math.min(totalPages - 1, start + 3);
  for (let i = start; i <= end; i++) pageNumbers.push(i);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* View toggle */}
      <div className="flex items-center justify-end gap-1 px-2 py-1.5 bg-[#FAFAFA] border border-b-0 border-[#E8E8E8]">
        <button
          title="Flat list"
          onClick={() => onGroupedChange(false)}
          className={cn('h-7 w-7 flex items-center justify-center rounded transition-colors', !grouped ? 'bg-[#FFCD00] text-[#262626]' : 'text-[#909090] hover:text-[#262626] hover:bg-[#F0F0F0]')}
        >
          <List className="h-4 w-4" />
        </button>
        <button
          title="Group by charger"
          onClick={() => onGroupedChange(true)}
          className={cn('h-7 w-7 flex items-center justify-center rounded transition-colors', grouped ? 'bg-[#FFCD00] text-[#262626]' : 'text-[#909090] hover:text-[#262626] hover:bg-[#F0F0F0]')}
        >
          <Layers className="h-4 w-4" />
        </button>
      </div>

      {/* Table */}
      <div className="border-x border-t bg-white flex-1 min-h-0" style={{ overflow: 'scroll' }}>
        <table className="w-full min-w-max table-auto text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th
                    key={header.id}
                    className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.background, GRID_STYLES.header.text, 'whitespace-nowrap border-b border-r border-[#D8D8D8] last:border-r-0')}
                    style={{ width: header.getSize() }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {grouped ? groups.map(group => {
              const isExpanded = expandedGroups.has(group.machineId);
              return (
                <>
                  <GroupHeaderRow
                    key={`grp-${group.machineId}`}
                    group={group}
                    colSpan={colCount}
                    expanded={isExpanded}
                    onToggle={() => toggleGroup(group.machineId)}
                    onAnalyzeAll={onAnalyzeAll}
                  />
                  {isExpanded && group.items.map((item, idx) => {
                    const row = rowById.get(item.irregularId);
                    if (!row) return null;
                    const isSelected = item.irregularId === selectedItemId;
                    const isAnomalous = item.delta < 0 || Math.abs(item.delta) > 50;
                    const isEven = idx % 2 === 0;
                    return (
                      <tr
                        key={item.irregularId}
                        onClick={() => onRowClick(item)}
                        className={cn(
                          'cursor-pointer transition-colors',
                          isSelected
                            ? cn(GRID_STYLES.row.selected, GRID_STYLES.row.selectedHover)
                            : isAnomalous
                            ? 'bg-yellow-50 hover:bg-yellow-100'
                            : isEven ? 'bg-white hover:bg-[#FFFBEB]' : 'bg-[#FAFAFA] hover:bg-[#FFFBEB]'
                        )}
                      >
                        {/* Indent the first cell to visually connect to the group */}
                        {row.getVisibleCells().map((cell, i) => (
                          <td
                            key={cell.id}
                            className={cn(
                              GRID_STYLES.cell.compact,
                              'whitespace-nowrap border-b border-[#E2E2E2]',
                              i === 0 ? 'pl-8 pr-4' : 'px-4'
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </>
              );
            }) : table.getRowModel().rows.map((row, idx) => {
              const isSelected = row.original.irregularId === selectedItemId;
              const isAnomalous = row.original.delta < 0 || Math.abs(row.original.delta) > 50;
              const isEven = idx % 2 === 0;
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowClick(row.original)}
                  className={cn(
                    'cursor-pointer transition-colors',
                    isSelected
                      ? cn(GRID_STYLES.row.selected, GRID_STYLES.row.selectedHover)
                      : isAnomalous
                      ? 'bg-yellow-50 hover:bg-yellow-100'
                      : isEven ? 'bg-white hover:bg-[#FFFBEB]' : 'bg-[#FAFAFA] hover:bg-[#FFFBEB]'
                  )}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className={cn(GRID_STYLES.cell.compact, 'px-4 whitespace-nowrap border-b border-[#E2E2E2]')}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 py-3 border border-[#E8E8E8] bg-white">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-[#E8E8E8] text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-[#E8E8E8] text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {pageNumbers.map(p => (
            <Button
              key={p}
              variant="outline"
              size="sm"
              className={cn(
                'h-8 w-8 p-0 border-[#E8E8E8] text-[#262626]',
                p === currentPage
                  ? 'bg-[#FFCD00] hover:bg-[#E6B800] border-[#FFCD00]'
                  : 'hover:bg-[#FFFBEB]'
              )}
              onClick={() => onPageChange(p)}
            >
              {p + 1}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-[#E8E8E8] text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-[#E8E8E8] text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 text-sm text-[#606060]">
            {currentPage + 1} of {totalPages} pages
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#606060]">{startItem}–{endItem} of {totalCount}</span>
          <span className="text-sm text-[#606060]">Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={v => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(s => (
                <SelectItem key={s} value={s.toString()}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
